"use client";

import { useEffect, useState } from "react";
import type { PostSection } from "@/data/posts";

/**
 * Article body + table of contents.
 * Sticky TOC on the left at lg+, body on the right.
 * Active section detected via IntersectionObserver and highlighted in the TOC.
 */
export function PostBody({ sections }: { sections: PostSection[] }) {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? "");

  useEffect(() => {
    const elements = sections
      .map((s) => document.getElementById(s.id))
      .filter((el): el is HTMLElement => Boolean(el));
    if (elements.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveId(visible.target.id);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: 0 },
    );
    elements.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [sections]);

  return (
    <div className="lg:grid lg:grid-cols-12 lg:gap-12">
      {/* TOC */}
      <aside className="hidden lg:block lg:col-span-3" aria-label="On this page">
        <div className="sticky top-28">
          <p className="text-small font-semibold uppercase tracking-[0.2em] text-text-muted">
            On this page
          </p>
          <ul className="mt-5 space-y-1">
            {sections.map((s) => {
              const isActive = activeId === s.id;
              return (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className={[
                      "relative block rounded-md py-2 pl-4 pr-3 text-small transition-colors",
                      isActive
                        ? "text-accent font-semibold"
                        : "text-text-muted hover:text-accent",
                    ].join(" ")}
                  >
                    <span
                      aria-hidden="true"
                      className={[
                        "absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-full transition-all duration-200",
                        isActive ? "h-5 bg-accent" : "h-0 bg-accent",
                      ].join(" ")}
                    />
                    {s.heading}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </aside>

      {/* Article body */}
      <article className="lg:col-span-9 max-w-prose">
        {sections.map((s) => (
          <section key={s.id} id={s.id} className="scroll-mt-28 mb-12 last:mb-0">
            <h2 className="text-h2 mb-5">{s.heading}</h2>
            {s.paragraphs.map((p, i) => (
              <p key={i} className="text-lead text-text-muted mb-5 leading-relaxed">
                {p}
              </p>
            ))}
          </section>
        ))}
      </article>
    </div>
  );
}
